import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Tag, Share2 } from 'lucide-react';
import UseAxiosPublic from '../hooks/UseAxiosPublic';
import SEO from '../components/SEO';

const SingleBlogPublic = () => {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const axiosPublic = UseAxiosPublic();

    useEffect(() => {
        const fetchSingleBlog = async () => {
            try {
                const response = await axiosPublic.get(`/blogs/post/${slug}`);
                console.log('response',response)
                if (response.data.success) {
                    setBlog(response.data.data);
                }
            } catch (err) {
                console.error('Error loading post:', err);
                setError('Blog post not found.');
            } finally {
                setLoading(false);
            }
        };

        fetchSingleBlog();
    }, [slug, axiosPublic]);

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 pt-32 pb-16 px-4 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error || !blog) {
        return (
            <div className="min-h-screen bg-slate-950 pt-32 pb-16 px-4 text-center">
                <h1 className="text-2xl font-bold text-white mb-4">Post Not Found</h1>
                <p className="text-slate-400 mb-6">The article you are looking for does not exist or has been removed.</p>
                <Link to="/blog" className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl inline-flex items-center gap-2">
                    <ArrowLeft size={18} />
                    Back to Blog
                </Link>
            </div>
        );
    }

    // Google Structured Data (JSON-LD Article Schema)
    const jsonLdSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": blog.title,
        "image": [blog.thumbnail],
        "datePublished": blog.createdAt,
        "dateModified": blog.updatedAt,
        "author": {
            "@type": "Organization",
            "name": blog.author || "Getsoko"
        },
        "publisher": {
            "@type": "Organization",
            "name": "Getsoko",
            "logo": {
                "@type": "ImageObject",
                "url": "https://getsoko.app/logo.png"
            }
        },
        "description": blog.metaDescription
    };

    return (
        <div className="min-h-screen bg-slate-950 pt-28 pb-20 px-4 sm:px-8 lg:px-16 text-slate-300 font-sans">
            {/* Dynamic SEO Metadata */}
            <SEO 
                title={blog.metaTitle || blog.title}
                description={blog.metaDescription}
                keywords={blog.keywords}
                image={blog.thumbnail}
                url={`https://getsoko.app/blog/${blog.slug}`}
            />

            {/* Google Schema Script Injection */}
            <script type="application/ld+json">
                {JSON.stringify(jsonLdSchema)}
            </script>

            <article className="max-w-4xl mx-auto">
                {/* Back Link */}
                <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 font-bold mb-8 transition-colors">
                    <ArrowLeft size={16} />
                    Back to all articles
                </Link>

                {/* Keywords Header Tags */}
                {blog.keywords && blog.keywords.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                        {blog.keywords.map((kw, idx) => (
                            <span key={idx} className="text-xs font-bold text-blue-400 bg-blue-500/10 px-3 py-1 rounded-md border border-blue-500/20 flex items-center gap-1">
                                <Tag size={12} />
                                {kw}
                            </span>
                        ))}
                    </div>
                )}

                {/* Main Article Title */}
                <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight mb-6">
                    {blog.title}
                </h1>

                {/* Author & Date Metadata */}
                <div className="flex items-center justify-between border-y border-slate-800/80 py-4 mb-8 text-sm text-slate-400">
                    <div className="flex items-center gap-6">
                        <span className="flex items-center gap-2">
                            <User size={16} className="text-blue-500" />
                            {blog.author || 'Getsoko Team'}
                        </span>
                        <span className="flex items-center gap-2">
                            <Calendar size={16} className="text-blue-500" />
                            {new Date(blog.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </span>
                    </div>

                    <button 
                        onClick={() => navigator.clipboard.writeText(window.location.href)}
                        className="p-2 hover:bg-slate-900 rounded-lg text-slate-400 hover:text-white transition-colors flex items-center gap-2 text-xs"
                        title="Copy Article Link"
                    >
                        <Share2 size={16} />
                        <span className="hidden sm:inline">Share</span>
                    </button>
                </div>

                {/* Main Thumbnail Banner */}
                <div className="w-full h-[250px] sm:h-[420px] rounded-2xl overflow-hidden mb-10 border border-slate-800 shadow-2xl">
                    <img 
                        src={blog.thumbnail} 
                        alt={blog.title} 
                        className="w-full h-full object-cover"
                    />
                </div>

                {/* Article Content Container */}
                <div 
                    className="prose prose-invert max-w-none text-slate-300 text-base sm:text-lg leading-relaxed space-y-6"
                    dangerouslySetInnerHTML={{ __html: blog.content }}
                ></div>
            </article>
        </div>
    );
};

export default SingleBlogPublic;